class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
    this.footerHideClass = "wt-product__sticky-buy--footer-hide";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    // Keep sticky bar outside transformed ancestors so position:fixed
    // still sticks to the viewport while scrolling.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    const productSection =
      document.querySelector(".wt-product") ||
      document.querySelector("[id^='MainProduct']") ||
      document.querySelector("section.main-product, .shopify-section .wt-product");
    const addToCartModule =
      (productSection &&
        productSection.querySelector(
          ".wt-product__add-to-cart, product-form.wt-product__add-to-cart, .main-product__buy-buttons--container",
        )) ||
      document.querySelector(".wt-product__add-to-cart");
    const footer =
      document.querySelector("footer.wt-footer") ||
      document.querySelector(".wt-footer-page-section") ||
      document.querySelector("footer");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    let atcInView = false;
    let footerInView = false;

    if (btn && addToCartModule) {
      btn.addEventListener("click", (e) => {
        if (!addToCart) {
          e.preventDefault();
        }
        addToCartModule.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }

    const isMobileSticky = () =>
      window.matchMedia("(max-width: 900px)").matches;

    // On mobile the in-page ATC is collapsed (height:0). A 0-height sentinel
    // still "intersects" near money-back / buy-box and wrongly hides sticky.
    const isAtcVisuallyPresent = () => {
      if (!addToCartModule) return false;
      const rect = addToCartModule.getBoundingClientRect();
      if (rect.height < 8 || rect.width < 8) return false;
      const style = window.getComputedStyle(addToCartModule);
      if (style.visibility === "hidden" || style.display === "none") return false;
      if (parseFloat(style.opacity || "1") === 0) return false;
      return true;
    };

    const setStickyVisible = (visible) => {
      if (visible) {
        this.classList.add(this.activeClass);
        this.classList.remove(this.footerHideClass);
        this.style.setProperty("opacity", "1", "important");
        this.style.setProperty("visibility", "visible", "important");
        this.style.setProperty("transform", "translateY(0)", "important");
        this.style.setProperty("display", "block", "important");
        this.style.setProperty("pointer-events", "auto", "important");
        this.style.setProperty("z-index", "10040", "important");
      } else {
        this.classList.remove(this.activeClass);
        this.classList.add(this.footerHideClass);
        this.style.setProperty("opacity", "0", "important");
        this.style.setProperty("visibility", "hidden", "important");
        this.style.setProperty("transform", "translateY(110%)", "important");
        this.style.setProperty("pointer-events", "none", "important");
      }
    };

    const syncIntersectionFlags = () => {
      // Body is position:fixed while cart is open, which makes IntersectionObserver
      // readings stale. Re-measure from layout after cart/menu closes.
      if (addToCartModule) {
        const rect = addToCartModule.getBoundingClientRect();
        atcInView =
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          rect.height >= 8;
      }
      if (footer) {
        const rect = footer.getBoundingClientRect();
        const vh = window.innerHeight;
        // Match footer observer intent (threshold + rootMargin ~ -35%)
        footerInView =
          rect.top < vh * 0.65 && rect.bottom > vh * 0.15;
      }
    };

    const updateVisibility = () => {
      if (document.body.classList.contains("menu-open")) {
        setStickyVisible(false);
        return;
      }
      // NOTE: intentionally do NOT hide sticky when the cart drawer is open.
      // Keep it visible on mobile + desktop while cart is open.
      // Hide only at footer. On mobile keep sticky through money-back / routine.
      if (footerInView) {
        setStickyVisible(false);
        return;
      }
      if (isMobileSticky()) {
        setStickyVisible(true);
        return;
      }
      setStickyVisible(!(atcInView && isAtcVisuallyPresent()));
    };

    const refreshAfterOverlayClose = () => {
      // Wait for cart unlock scroll restore, then re-show sticky if appropriate
      requestAnimationFrame(() => {
        syncIntersectionFlags();
        updateVisibility();
        requestAnimationFrame(() => {
          syncIntersectionFlags();
          updateVisibility();
        });
      });
    };

    if (addToCartModule) {
      const atcObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            atcInView = entry.isIntersecting;
          });
          updateVisibility();
        },
        { threshold: 0.05 },
      );
      atcObserver.observe(addToCartModule);
    } else {
      atcInView = false;
    }

    if (footer) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            footerInView = entry.isIntersecting;
          });
          updateVisibility();
        },
        {
          // Only hide when footer is meaningfully in view (not early false positives)
          root: null,
          threshold: 0.15,
          rootMargin: "0px 0px -35% 0px",
        },
      );
      footerObserver.observe(footer);
    }

    updateVisibility();

    // Keep sticky in sync with menu / cart overlay body classes
    const menuOpenObserver = new MutationObserver((mutations) => {
      const classChanged = mutations.some(
        (m) => m.attributeName === "class",
      );
      if (!classChanged) return;

      const cartOpen = document.body.classList.contains(
        "page-overlay-cart-on",
      );
      const menuOpen = document.body.classList.contains("menu-open");

      if (cartOpen || menuOpen) {
        updateVisibility();
        return;
      }

      // Cart/menu just closed — IO flags may be wrong after body scroll-lock
      refreshAfterOverlayClose();
    });
    menuOpenObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    document.addEventListener("cart-drawer-close", refreshAfterOverlayClose);
    window.addEventListener("resize", updateVisibility);
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
